import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, take } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';

function waitForAuth(authService: AuthService, router: Router) {
  return toObservable(authService.isLoading).pipe(
    filter((loading) => !loading),
    take(1),
    map(() => authService.currentUser()),
  );
}

/** Protegge le rotte riservate agli utenti autenticati. */
export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return waitForAuth(authService, router).pipe(
    map((user) => {
      if (!user) return router.createUrlTree(['/login']);
      if (!user.passwordChanged) return router.createUrlTree(['/change-password']);
      return true;
    }),
  );
};

/** Redirige gli utenti già autenticati lontano dalla pagina di login. */
export const loginGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return waitForAuth(authService, router).pipe(
    map((user) => {
      if (!user) return true;
      if (!user.passwordChanged) return router.createUrlTree(['/change-password']);
      return router.createUrlTree([user.role === 'agent' ? '/agent' : '/admin']);
    }),
  );
};

/** Consente l'accesso alla pagina di cambio password solo agli utenti autenticati che devono ancora cambiarla. */
export const changePasswordGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return waitForAuth(authService, router).pipe(
    map((user) => {
      if (!user) return router.createUrlTree(['/login']);
      if (user.passwordChanged) return router.createUrlTree([user.role === 'agent' ? '/agent' : '/admin']);
      return true;
    }),
  );
};

/** Protegge le rotte backoffice (admin + power_user). */
export const adminGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = authService.currentUser();
  if (user?.role !== 'admin' && user?.role !== 'power_user') return router.createUrlTree(['/agent']);
  return true;
};

/** Protegge le rotte riservate ai soli admin (non accessibili a power_user). */
export const adminOnlyGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = authService.currentUser();
  if (user?.role !== 'admin') return router.createUrlTree(['/admin/visits-export']);
  return true;
};

/** Protegge le rotte agent. */
export const agentGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const user = authService.currentUser();
  if (user?.role !== 'agent') return router.createUrlTree(['/admin']);
  return true;
};

/**
 * Blocca l'accesso a /agent e /admin se l'utente non ha accettato la versione corrente dei ToU.
 * Redirect a /accept-terms?returnUrl=<url corrente>.
 * Se touVersion non è impostato in Config/global, la feature è disattivata e il guard lascia passare.
 * Dipende da authGuard (che garantisce user autenticato e passwordChanged).
 */
export const touGuard: CanActivateFn = async (_route, state) => {
  const authService = inject(AuthService);
  const configService = inject(ConfigService);
  const router = inject(Router);

  const user = authService.currentUser();
  if (!user) return router.createUrlTree(['/login']);

  const config = await configService.getTouConfig();
  if (!config.touVersion) return true; // feature disattivata
  if (user.touAcceptedVersion === config.touVersion) return true;

  return router.createUrlTree(['/accept-terms'], { queryParams: { returnUrl: state.url } });
};

/**
 * Protegge la route /accept-terms.
 * Redirect a home se l'utente ha già accettato la versione corrente.
 * Redirect a /login se non autenticato.
 */
export const acceptTermsGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const configService = inject(ConfigService);
  const router = inject(Router);

  return waitForAuth(authService, router).pipe(
    switchMap(async (user) => {
      if (!user) return router.createUrlTree(['/login']);
      if (!user.passwordChanged) return router.createUrlTree(['/change-password']);
      const config = await configService.getTouConfig();
      if (!config.touVersion || user.touAcceptedVersion === config.touVersion) {
        const dest = user.role === 'agent' ? '/agent' : '/admin';
        return router.createUrlTree([dest]);
      }
      return true as true;
    }),
  );
};
